import {
  ActionError,
  Button,
  Checkbox,
  Chip,
  ChoiceRow,
  FormFooter,
  PageHeader,
  PageTitle,
  ReadingColumn,
  SearchInput,
  Skeleton,
  toast,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { useDebounced } from '@/lib/use-debounced'
import { routes } from '@/routes'
import {
  useCreateRegion,
  useRegions,
  useTreatmentPlan,
  useUpdateProduct,
  type Product,
  type Region,
  type TreatmentPlan,
} from './api'
import { productName } from './labels'

/**
 * Regiões de aplicação (docs/areas/planos-de-tratamento.md, "Regiões de aplicação"; RN-PLA-11):
 * coluna de 640px, chips das escolhidas no topo, busca em foco automático, grupos FACIAL · 24 e
 * CORPORAL · 31 com o nome técnico e o popular, e "Da clínica". O que não está na lista é escrito
 * à mão e vira região personalizada da clínica. "Concluir com 3" grava a lista inteira no produto.
 */

const REGION_LIMIT = 20

interface Chosen {
  regionId: string | null
  name: string
  label: string
}

const normalize = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()

export function RegionsPage() {
  const { id, productId } = useParams()
  const query = useTreatmentPlan(id)
  const plan = query.data
  const product = plan?.products.find((p) => p.id === productId)

  if (!plan || !product) {
    return (
      <>
        <PageHeader
          LinkComponent={Link}
          crumbs={[
            { label: 'Planos de Tratamento', href: routes.treatmentPlans },
            { label: 'Produto' },
            { label: 'Regiões de aplicação' },
          ]}
        />
        <Page>
          <ReadingColumn>
            {query.isError ? (
              <LoadError
                title="Não conseguimos abrir o produto"
                error={query.error}
                onRetry={() => void query.refetch()}
              />
            ) : query.isSuccess && !product ? (
              <ActionError
                title="Este produto não está mais no plano"
                description="Ele pode ter sido arquivado em outra tela. Volte ao plano para ver os produtos atuais."
                actions={
                  <Button variant="outline" size="sm" asChild>
                    <Link to={routes.treatmentPlanTab(plan?.id ?? id ?? '', 'products')}>
                      Voltar ao plano
                    </Link>
                  </Button>
                }
              />
            ) : (
              <div role="status" aria-label="Carregando" className="flex flex-col gap-4">
                <Skeleton className="h-[26px] w-[280px]" />
                <Skeleton className="h-[44px] w-full" />
                <Skeleton className="h-[280px] w-full" />
              </div>
            )}
          </ReadingColumn>
        </Page>
      </>
    )
  }
  return <RegionsEditor key={product.id} plan={plan} product={product} />
}

function RegionsEditor({ plan, product }: { plan: TreatmentPlan; product: Product }) {
  const navigate = useNavigate()
  const [chosen, setChosen] = useState<Chosen[]>(() =>
    product.regions.map((r) => ({ regionId: r.regionId, name: r.name, label: r.label })),
  )
  const [search, setSearch] = useState('')
  const term = useDebounced(search.trim())
  const regions = useRegions(term)
  const create = useCreateRegion()
  const update = useUpdateProduct(plan.id)
  const [problem, setProblem] = useState<{ message: string; suggested: Region | null } | null>(null)
  const back = routes.treatmentPlanTab(plan.id, 'products')
  const readOnly = plan.status !== 'open' || plan.clinic !== null

  const isChosen = (region: Region) =>
    chosen.some((c) =>
      c.regionId ? c.regionId === region.id : normalize(c.name) === normalize(region.name),
    )

  function toggle(region: Region) {
    if (isChosen(region)) {
      setChosen((current) =>
        current.filter(
          (c) =>
            !(c.regionId ? c.regionId === region.id : normalize(c.name) === normalize(region.name)),
        ),
      )
      return
    }
    if (chosen.length >= REGION_LIMIT) {
      toast(`Este produto já tem ${REGION_LIMIT} regiões, o máximo.`)
      return
    }
    setChosen((current) => [
      ...current,
      { regionId: region.id, name: region.name, label: region.label },
    ])
  }

  function addCustom() {
    const name = term.slice(0, 80)
    if (name.length < 2) return
    if (chosen.length >= REGION_LIMIT) {
      toast(`Este produto já tem ${REGION_LIMIT} regiões, o máximo.`)
      return
    }
    setProblem(null)
    create.mutate(name, {
      onSuccess: (region) => {
        if (!isChosen(region))
          setChosen((current) => [
            ...current,
            { regionId: region.id, name: region.name, label: region.label },
          ])
        setSearch('')
      },
      onError: (error) => {
        const suggested =
          isApiError(error) && error.code === 'conflict'
            ? (((error.details.region ?? error.details.existing) as Region | undefined) ?? null)
            : null
        setProblem({ message: errorMessage(error), suggested })
      },
    })
  }

  function conclude() {
    update.mutate(
      {
        productId: product.id,
        version: product.version,
        input: {
          regions: chosen.map((c) => (c.regionId ? { regionId: c.regionId } : { name: c.name })),
        },
      },
      {
        onSuccess: () => navigate(back),
        onError: (error) => setProblem({ message: errorMessage(error), suggested: null }),
      },
    )
  }

  const groups = regions.data?.groups ?? []
  const allRegions = groups.flatMap((g) => g.regions)
  const exactMatch =
    term.length >= 2 &&
    allRegions.some(
      (r) => normalize(r.name) === normalize(term) || normalize(r.label) === normalize(term),
    )
  const canAddCustom = !readOnly && term.length >= 2 && !exactMatch && !regions.isPending

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Planos de Tratamento', href: routes.treatmentPlans },
          { label: productName(product), href: back },
          { label: 'Regiões de aplicação' },
        ]}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to={back}>Voltar</Link>
          </Button>
        }
      />
      <Page>
        <ReadingColumn>
          <PageTitle
            title="Regiões de aplicação"
            subtitle={`${productName(product)} · ${chosen.length === 1 ? '1 selecionada' : `${chosen.length} selecionadas`}`}
          />

          {chosen.length > 0 ? (
            <div className="mt-5 flex flex-wrap items-center gap-2" aria-label="Regiões escolhidas">
              {chosen.map((c, index) => (
                <Chip
                  key={`${c.regionId ?? c.name}-${index}`}
                  onRemove={
                    readOnly
                      ? undefined
                      : () => setChosen((current) => current.filter((_, i) => i !== index))
                  }
                >
                  {c.label}
                </Chip>
              ))}
            </div>
          ) : null}

          <div className="mt-5">
            <SearchInput
              autoFocus
              value={search}
              onChange={setSearch}
              placeholder="Buscar região — 'lábio', 'mento', 'abdômen'…"
              aria-label="Buscar região"
            />
          </div>

          {problem ? (
            <ActionError
              className="mt-4"
              title="Essa região já existe"
              description={problem.message}
              onDismiss={() => setProblem(null)}
              actions={
                problem.suggested ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (problem.suggested) toggle(problem.suggested)
                      setProblem(null)
                      setSearch('')
                    }}
                  >
                    Selecionar {problem.suggested.label}
                  </Button>
                ) : undefined
              }
            />
          ) : null}

          <div className="mt-4">
            {regions.isError ? (
              <LoadError
                title="Não conseguimos carregar as regiões"
                error={regions.error}
                onRetry={() => void regions.refetch()}
              />
            ) : regions.isPending ? (
              <div role="status" aria-label="Carregando as regiões" className="flex flex-col gap-2">
                <Skeleton className="h-[48px] w-full" />
                <Skeleton className="h-[48px] w-full" />
                <Skeleton className="h-[48px] w-full" />
              </div>
            ) : (
              <div className="border-hairline bg-surface border">
                {groups.map((group) => (
                  <div key={group.group}>
                    <div className="border-hairline bg-pampas eyebrow flex items-baseline justify-between border-b px-4 py-[10px]">
                      <span>{group.title}</span>
                      <span className="text-label-soft tracking-normal">{group.total}</span>
                    </div>
                    {group.regions.length === 0 ? (
                      <p className="font-body text-secondary px-4 py-3 text-[12px] leading-[1.6] font-light">
                        Nenhuma região deste grupo para "{term}".
                      </p>
                    ) : (
                      group.regions.map((region) => (
                        <ChoiceRow
                          key={region.id}
                          bare
                          control={
                            <Checkbox
                              checked={isChosen(region)}
                              disabled={readOnly}
                              onCheckedChange={() => toggle(region)}
                              aria-label={region.name}
                            />
                          }
                        >
                          {region.name}
                          {region.commonName ? (
                            <span className="text-secondary"> ({region.commonName})</span>
                          ) : null}
                        </ChoiceRow>
                      ))
                    )}
                  </div>
                ))}
                {canAddCustom ? (
                  <button
                    type="button"
                    onClick={addCustom}
                    disabled={create.isPending}
                    className="font-body text-ink hover:bg-pampas min-h-touch flex w-full cursor-pointer items-center px-4 py-[14px] text-left text-[14px] leading-[1.3] font-light"
                  >
                    {create.isPending
                      ? 'Adicionando…'
                      : `+ Adicionar "${term}" como região da clínica`}
                  </button>
                ) : null}
                {groups.length === 0 && !canAddCustom ? (
                  <p className="font-body text-secondary px-4 py-6 text-[13px] leading-[1.6] font-light">
                    Nenhuma região encontrada. Digite pelo menos 2 letras para criar uma região da
                    clínica.
                  </p>
                ) : null}
              </div>
            )}
          </div>

          <FormFooter
            className="mt-6"
            status={chosen.length === 0 ? 'Escolha pelo menos uma região de aplicação.' : undefined}
            secondary={
              <Button variant="outline" asChild>
                <Link to={back}>Cancelar</Link>
              </Button>
            }
            primary={
              <Button
                disabled={readOnly || chosen.length === 0}
                loading={update.isPending}
                loadingLabel="Gravando…"
                onClick={conclude}
              >
                {chosen.length === 0 ? 'Escolha uma região' : `Concluir com ${chosen.length}`}
              </Button>
            }
          />
        </ReadingColumn>
      </Page>
    </>
  )
}
